import useSWR, { useSWRConfig } from 'swr';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import { fetcher } from '@/utils/services';

/**
 * Takes an array of constituent IDs and returns their constituent data,
 * checking first with the SWR cache to see if data has already been fetched and
 * if not then fetching the constituent and adding it to the cache.
 */
const useConstituents = (constituentIds, swrOptions) => {
  const { cache, mutate } = useSWRConfig();
  const ownerOrgId = useOwnerOrgId();
  const fetchConstituents = async (ids) => {
    const constituents = await Promise.all(
      ids.map((constituentId) => {
        const key = `orgs/${ownerOrgId}/constituents/${constituentId}`;

        /**
         * It is possible for a constituent to be cached in 2 different ways: one with
         * `fetcher` and another with `fetcherWithDataKey`. Those store slightly
         * different versions of the AJAX response. To get around that, check for
         * either `.constituent` in `data` or return `data` itself.
         */
        const cacheData = cache.get(key)?.data;
        const cachedConstituent = cacheData?.constituent || cacheData;

        return (
          cachedConstituent ||
          (async () => {
            const uncachedConstituent = (await fetcher(key)).constituent;
            mutate(key, uncachedConstituent);
            return uncachedConstituent;
          })()
        );
      }),
    );
    return constituents;
  };
  return useSWR(
    ownerOrgId && constituentIds?.length ? constituentIds : null,
    fetchConstituents,
    swrOptions,
  );
};

export default useConstituents;
